import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { ClienteDropDown } from "../../../../common/domain/models/cliente_dropdown";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePesquisaClienteListaPagination } from "../hooks/PesquisaCliente_lista/pagination";
import { initializePesquisaClienteListaState, usePesquisaClienteListaFilters } from "../hooks/PesquisaCliente_lista/filters";
import { SatiscacaoClienteLista } from "../../domain/models/satisfacao_cliente_lista";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { SatisfacaoClienteForm } from "../schemas/PesquisaCliente_lista/satisfacao_cliente";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

/**
 * LoadPesquisaSatisfacaoClientesFuncState
 */

type LoadPesquisaSatisfacaoClientesFuncState = {
  centroCustos?: number[] | null;
  status?: number | null;
  clientes?: number[] | null;
  pesquisaCliente?: string | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * usePesquisaClienteListaViewModel
 */

export const usePesquisaClienteListaViewModel = () => {

  const {
    entregaObras: {
      getAllSatisfacaoClienteUseCase,
      createSatisfacaoClienteUseCase,
      updateSatisfacaoClienteUseCase,
      deleteSatisfacaoClienteUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadClientesDropDown,
  } = useDropDownLoader();

  const backOrNavigate = useBackOrNavigate();

  const debouncedHandlerRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [pesquisaClientes, setPesquisaClientes] = useState<SatiscacaoClienteLista[]>();
  const [selectedPesquisaCliente, setSelectedPesquisaCliente] = useState<SatiscacaoClienteLista>();

  const [centroCustosDropdown, setCentroCustosDropdown] = useState<CentroCustoDropDown[]>();
  const [clientesDropdown, setClientesDropdown] = useState<ClienteDropDown[]>();

  const [safistacaoClienteModal, setSafistacaoClienteModal] = useState(false);
  const [safistacaoClienteDeleteModal, setSafistacaoClienteDeleteModal] = useState(false);

  const filters = usePesquisaClienteListaFilters();
  const pagination = usePesquisaClienteListaPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  const resultadoMedio = useMemo(() => {
    if (pesquisaClientes === undefined || pesquisaClientes.length === 0) return 0;
    return pesquisaClientes[0].avarageTotal ?? 0;
  }, [
    pesquisaClientes,
  ]);

  /**
   * Funções de carregamento
   */

  const loadPesquisaSatisfacaoClientes = useCallback(async (fs?: LoadPesquisaSatisfacaoClientesFuncState) => {
    try {
      const filteredCentroCustos = evaluateParamValue(fs?.centroCustos, filters.centroCustos);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);
      const filteredClientes = evaluateParamValue(fs?.clientes, filters.clientes);
      const filteredPesquisaCliente = evaluateParamValue(fs?.pesquisaCliente, filters.pesquisaCliente);
      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllSatisfacaoClienteUseCase.getAllSatisfacaoCliente(
        filteredCentroCustos,
        filteredStatus,
        filteredClientes,
        filteredPesquisaCliente,
        filteredPage,
        filteredRowsPerPage,
      );

      setPesquisaClientes(getAllSatisfacaoClienteUseCase.items);
      pagination.setTotalItems(getAllSatisfacaoClienteUseCase.totalItems);
    } catch (e: unknown) {
      setPesquisaClientes(undefined);
      throw new Error('Falha ao buscar pesquisa de satisfação dos clientes!');
    }
  }, [
    filters,
    pagination,
    getAllSatisfacaoClienteUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadClientesDropDown(dt => setClientesDropdown(dt));

      await loadPesquisaSatisfacaoClientes();

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadClientesDropDown(dt => setClientesDropdown(dt));

      await loadPesquisaSatisfacaoClientes();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadClientesDropDown,
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handleSatisfacaoClienteModalOpenState = useCallback((idSatisfacaoCliente: number | null, openState: boolean) => {
    if (openState) {
      if (idSatisfacaoCliente && pesquisaClientes) {
        setSelectedPesquisaCliente(pesquisaClientes.find(x => x.idSatisfacaoCliente === idSatisfacaoCliente));
      } else {
        setSelectedPesquisaCliente(undefined);
      }
    }
    setSafistacaoClienteModal(openState);
  }, [
    pesquisaClientes,
  ]);

  const handleSatisfacaoClienteSubmit = useCallback(async (form: SatisfacaoClienteForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedPesquisaCliente) {
        await updateSatisfacaoClienteUseCase.updateSatisfacaoCliente(
          selectedPesquisaCliente.idSatisfacaoCliente,
          form.status,
          form.centroCusto,
          form.cliente,
          form.nomeCliente,
          form.data,
          form.preencher,
        );
      } else {
        await createSatisfacaoClienteUseCase.createSatisfacaoCliente(
          form.status,
          form.centroCusto,
          form.cliente,
          form.nomeCliente,
          form.data,
          form.preencher,
        );
      }

      await loadPesquisaSatisfacaoClientes();

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

      setSelectedPesquisaCliente(undefined);
      setSafistacaoClienteModal(false);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedPesquisaCliente,
    updateSatisfacaoClienteUseCase,
    createSatisfacaoClienteUseCase,
    loadPesquisaSatisfacaoClientes,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleSatisfacaoClienteDeleteModalOpenState = useCallback((idSatisfacaoCliente: number | null, openState: boolean) => {
    if (openState) {
      if (idSatisfacaoCliente && pesquisaClientes) {
        setSelectedPesquisaCliente(pesquisaClientes.find(x => x.idSatisfacaoCliente === idSatisfacaoCliente));
      } else {
        setSelectedPesquisaCliente(undefined);
      }
    }
    setSafistacaoClienteDeleteModal(openState);
  }, [
    pesquisaClientes,
  ]);

  const handleSatisfacaoClienteDeleteSubmit = useCallback(async () => {
    if (selectedPesquisaCliente) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteSatisfacaoClienteUseCase.deleteSatisfacaoCliente(
          selectedPesquisaCliente.idSatisfacaoCliente,
        );

        await loadPesquisaSatisfacaoClientes();

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');

        setSelectedPesquisaCliente(undefined);
        setSafistacaoClienteDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedPesquisaCliente,
    deleteSatisfacaoClienteUseCase,
    loadPesquisaSatisfacaoClientes,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleSatisfacaoClienteVisualizar = useCallback((idSatisfacaoCliente: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/SatisfacaoClientePesquisa_lista?id=${idSatisfacaoCliente}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/SatisfacaoClientePesquisa_lista?id=${idSatisfacaoCliente}`);
    }
  }, [
    backOrNavigate,
  ]);

  const handlePreencherChange = useCallback(async (preencher: boolean) => {
    try {
      setLoading(true);
      usePesquisaClienteListaFilters.setState({ preencher, clientes: null, pesquisaCliente: null });
      usePesquisaClienteListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoClientes({ clientes: null, pesquisaCliente: null, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handleCentroCustosChange = useCallback(async (centroCustos: number[] | null) => {
    try {
      setLoading(true);
      usePesquisaClienteListaFilters.setState({ centroCustos });
      usePesquisaClienteListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoClientes({ centroCustos, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number) => {
    try {
      setLoading(true);
      usePesquisaClienteListaFilters.setState({ status });
      usePesquisaClienteListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoClientes({ status, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handleClientesChange = useCallback(async (clientes: number[] | null) => {
    try {
      setLoading(true);
      usePesquisaClienteListaFilters.setState({ clientes });
      usePesquisaClienteListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoClientes({ clientes, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handlePesquisaClienteChange = useDebouncedCallback(debouncedHandlerRef, (pesquisaCliente: string | null) => {
    usePesquisaClienteListaFilters.setState({ pesquisaCliente });
    return async () => {
      try {
        setLoading(true);
        usePesquisaClienteListaFilters.setState({ pesquisaCliente });
        usePesquisaClienteListaPagination.setState({ page: 1 });
        await loadPesquisaSatisfacaoClientes({ pesquisaCliente, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      usePesquisaClienteListaFilters.setState({ ...initializePesquisaClienteListaState });
      usePesquisaClienteListaPagination.setState({ page: 1 });
      await loadPesquisaSatisfacaoClientes({ ...initializePesquisaClienteListaState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      usePesquisaClienteListaPagination.setState({ page: page + 1 });
      await loadPesquisaSatisfacaoClientes({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      usePesquisaClienteListaPagination.setState({ page: 1, rowsPerPage });
      await loadPesquisaSatisfacaoClientes({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisaSatisfacaoClientes,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedPesquisaCliente) {
      changeHistory.handleOpenChangeHistory(selectedPesquisaCliente.idSatisfacaoCliente, LogTipoTabela.SATISFACAO_CLIENTE, true);
    }
  }, [
    selectedPesquisaCliente,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    pesquisaClientes: pesquisaClientes ?? [],
    selectedPesquisaCliente: selectedPesquisaCliente ?? null,
    centroCustosDropdown: centroCustosDropdown ?? [],
    clientesDropdown: clientesDropdown ?? [],
    safistacaoClienteModal,
    safistacaoClienteDeleteModal,
    filters,
    pagination,
    changeHistory,
    resultadoMedio,
    handleReload,
    handleSatisfacaoClienteModalOpenState,
    handleSatisfacaoClienteSubmit,
    handleSatisfacaoClienteDeleteModalOpenState,
    handleSatisfacaoClienteDeleteSubmit,
    handleSatisfacaoClienteVisualizar,
    handlePreencherChange,
    handleCentroCustosChange,
    handleStatusChange,
    handleClientesChange,
    handlePesquisaClienteChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenChangeHistory,
  };
}